import Label from "components/Label/Label";
import React, { FC, useEffect } from "react";
import Avatar from "shared/Avatar/Avatar";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import CommonLayout from "./CommonLayout";
import { useUserSessionStore } from "../../stores/UserSession.store";
import { useForm } from "react-hook-form";
import { useUsers } from "../../hooks/collections/useUsers";
import { isFile } from "../../@core/utils/Types";
import { useFirebaseData } from "../../@shared/hooks/useFirebaseData";
import { ConvertUtil } from "../../@core/utils/Convert";
import { Collections } from "../../@core/constants/FirebasePaths";
import { useLoader } from "../../@shared/hooks/useLoader";
import { useAlert } from "../../@shared/hooks/useAlert";
import AccountPageCompany from "./AccountPageCompany";

export interface AccountPageProps {
  className?: string;
}

const AccountPage: FC<AccountPageProps> = ({ className = "" }) => {
  const loader = useLoader();
  const alert = useAlert();
  const user = useUserSessionStore((state) => state.user);
  const { register, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      imageUrl: user?.imageUrl,
      dateOfBirth: user?.dateOfBirth || "",
      address: user?.address || "",
      phone: user?.phone || "",
      name: user?.name || "",
      email: user?.email || ""
    }
  });
  const imageUrl = watch("imageUrl");
  const { updateUser } = useUsers({ loadData: false });
  const { uploadFileByBase64 } = useFirebaseData({ collectionPath: null, loadData: false });

  const handleSubmitForm = async (data: any) => {
    loader.start();
    try {
      if (data.imageUrl && isFile(data.imageUrl[0])) {
        const file = data.imageUrl[0];
        const base64 = await ConvertUtil.fileToBase64(file);
        data.imageUrl = await uploadFileByBase64(base64, `${Collections.users._path}/${user.id}/avatar`);
      }
      await updateUser(user.id, data);
      alert.success("Success", "User information updated successfully");
    } catch (e) {
      console.error(e);
      alert.error("Error", "An error occurred while updating the user information");
    } finally {
      loader.stop();
    }
  };

  useEffect(() => {
    setValue("imageUrl", user?.imageUrl);
    setValue("dateOfBirth", user?.dateOfBirth || "");
    setValue("address", user?.address || "");
    setValue("phone", user?.phone || "");
    setValue("name", user?.name || "");
    setValue("email", user?.email || "");
  }, [user]);

  if (!user) return <></>;
  if (user.role === "company") return <AccountPageCompany/>;

  return (
    <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
      <CommonLayout>
        <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-6 sm:space-y-8">
          {/* HEADING */}
          <h2 className="text-3xl font-semibold">Account information</h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
          <div className="flex flex-col md:flex-row">
            <div className="flex-shrink-0 flex items-start">
              <div className="relative rounded-full overflow-hidden flex">
                <Avatar
                  userName={user?.name}
                  imgUrl={imageUrl && isFile(imageUrl[0]) ? URL.createObjectURL(imageUrl[0] as any) : imageUrl || ''}
                  sizeClass="w-32 h-32"
                />
                <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-neutral-50 cursor-pointer">
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.5 5H7.5C6.83696 5 6.20107 5.26339 5.73223 5.73223C5.26339 6.20107 5 6.83696 5 7.5V20M5 20V22.5C5 23.163 5.26339 23.7989 5.73223 24.2678C6.20107 24.7366 6.83696 25 7.5 25H22.5C23.163 25 23.7989 24.7366 24.2678 24.2678C24.7366 23.7989 25 23.163 25 22.5V17.5M5 20L10.7325 14.2675C11.2013 13.7988 11.8371 13.5355 12.5 13.5355C13.1629 13.5355 13.7987 13.7988 14.2675 14.2675L17.5 17.5M25 12.5V17.5M25 17.5L23.0175 15.5175C22.5487 15.0488 21.9129 14.7855 21.25 14.7855C20.5871 14.7855 19.9513 15.0488 19.4825 15.5175L17.5 17.5M17.5 17.5L20 20M22.5 5H27.5M25 2.5V7.5M17.5 10H17.5125"
                      stroke="currentColor"
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>

                  <span className="mt-1 text-xs">Change Image</span>
                </div>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.avif"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  {...register("imageUrl")}
                />
              </div>
            </div>
            <div className="flex-grow mt-10 md:mt-0 md:pl-16 max-w-3xl space-y-6">
              <div>
                <Label>Name</Label>
                <Input className="mt-1.5" {...register("name")}/>
              </div>
              {/* ---- */}
              <div>
                <Label>Email</Label>
                <Input className="mt-1.5" {...register("email")} disabled/>
              </div>
              {/* ---- */}
              <div className="max-w-lg">
                <Label>Date of birth</Label>
                <Input
                  className="mt-1.5"
                  type="date"
                  defaultValue="1990-07-22"
                  {...register("dateOfBirth")}
                />
              </div>
              {/* ---- */}
              <div>
                <Label>Address</Label>
                <Input className="mt-1.5" {...register("address")} />
              </div>
              {/* ---- */}
              <div>
                <Label>Phone number</Label>
                <Input className="mt-1.5" {...register("phone")} />
              </div>
              <div className="pt-2">
                <ButtonPrimary>Update info</ButtonPrimary>
              </div>
            </div>
          </div>
        </form>
      </CommonLayout>
    </div>
  );
};

export default AccountPage;
